import React, { useEffect, useState } from 'react'
import { useResolvedNav } from 'config/navigation/useResolvedNav'
import { ArrowSquareLeft } from 'iconsax-react'
import { useHistory, useLocation } from 'react-router-dom'
import styled, { css } from 'styled-components'

import { ChevronDown } from 'components/lib/DesignSystem/Icons'
import Tag from 'components/lib/DesignSystem/Tag'
import Tooltip from 'components/lib/DesignSystem/V3/Tooltip'

import { navRowActive, navRowHover } from '../navStyles'

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const Root = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--color-elevated-solid);
  font-family: var(--t-typography-font-family-font-family-default);
`

// Back header — full-width rules above and below the row (Figma 1:33591 /
// 1:34698: border-t + border-b Blue/200). The 40px button sits between them.
const BackHeader = styled.div`
  flex-shrink: 0;
  border-top: 1px solid var(--color-border);
  border-bottom: 1px solid var(--color-border);
  padding: 0 3px;
`

// Back button — 40px tall, rounded 10px, with a bold primary arrow-square-left
// glyph. Figma 1:33563: the pale-blue (blue-7 / #eaefff) pill is the HOVER
// affordance only; at rest the row is transparent like the settings items
// below it (it previously carried the pale fill at rest, which read as a
// permanent hover state).
const BackButton = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 40px;
  padding: 0 9px;
  border-radius: 10px;
  box-sizing: border-box;
  cursor: pointer;
  font-family: var(--t-typography-font-family-font-family-default);
  font-size: 14px;
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 20px;
  color: var(--sidebar-item-text);
  background-color: transparent;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: var(--sidebar-item-hover-background);
  }

  &:focus-visible {
    outline: 2px solid var(--color-border);
    outline-offset: -2px;
  }
`

const BackLabel = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// The V3 Tooltip renders its trigger as a bare <span>, so this wrapper is NOT a
// direct flex item of BackButton and inline-flex leaves it sitting on a line box:
// the strut's descender adds ~3px under the 24px glyph, so centring that taller
// box lifts the arrow above the "Settings" label. Block-level flex kills the line
// box, and the fixed 24px box then centres against the text.
const BackIconWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
`

const ItemList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 8px 12px 16px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Item = styled.li`
  border-radius: 10px;
`

// A row inside the settings tree — used for direct pages, folder headers, and
// (indented) folder children. `$child` adds the L3 indent; `$folder` switches
// the active-bar accent off (folders only highlight on active descendant).
const rowBase = css`
  all: unset;
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 36px;
  padding: ${({ $child }) => ($child ? '8px 16px' : '8px 12px')};
  border-radius: 10px;
  cursor: pointer;
  font-family: var(--t-typography-font-family-font-family-default);
  font-size: 14px;
  font-weight: var(--t-typography-font-weight-regular);
  line-height: 20px;
  color: var(--sidebar-item-text);
  transition:
    background-color 0.15s ease,
    color 0.15s ease;
  box-sizing: border-box;

  svg,
  svg path {
    fill: currentColor;
  }

  ${navRowHover}

  ${({ $active }) => $active && navRowActive()}

  /* Active trail is Bold (design review 2026-08): a folder whose descendant is the
     current page bolds its header too (no active bar — that stays on the leaf). */
  ${({ $activeTrail }) =>
    $activeTrail &&
    css`
      font-weight: var(--t-typography-font-weight-bold);
    `}
`

const RowButton = styled.button`
  ${rowBase}
`

const RowLabel = styled.span`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const TagSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;
`

const ChevronWrap = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-left: auto;
  transition: transform 0.15s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};

  svg,
  svg path {
    fill: currentColor;
  }
`

// Children are inset from the parent row so the active pill starts at the
// indent (Figma: expanded "Email Encryption" → inset "Integration" pill).
const FolderChildren = styled.ul`
  list-style: none;
  margin: 4px 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

// The Settings section is the pinnedBottom node in the resolved tree.
const findSettingsNode = (tree) =>
  Array.isArray(tree) ? tree.find((n) => n.pinnedBottom) : undefined

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SettingsPanelView — full Settings sub-view.
 *
 * Renders the Settings navigation tree (Confluence "Navigation Menu" IA):
 *   - direct pages render as rows that navigate;
 *   - `folder` nodes render as expandable groups (chevron + indented L3 children).
 * The tree comes from the resolved nav config (single source of truth), so
 * gated nodes (e.g. partner-only MSP Settings) are filtered by `visible`.
 *
 * The top control reads "Settings" (Figma 626:10405) with a bold navy
 * arrow-square-left; hovering the arrow shows a "Back to main menu" tooltip and
 * clicking the row returns to the main rail.
 *
 * @param {object} props
 * @param {() => void} props.onBack - called when the back control is clicked
 */
const SettingsPanelView = ({ onBack }) => {
  const history = useHistory()
  const { pathname } = useLocation()
  const tree = useResolvedNav(undefined, pathname)
  const settingsNode = findSettingsNode(tree)
  const children = settingsNode && Array.isArray(settingsNode.children) ? settingsNode.children : []
  const visibleChildren = children.filter((c) => c.visible)

  // Folders expanded by default when they contain the active page.
  const [expandedFolders, setExpandedFolders] = useState(() => {
    const initial = {}
    visibleChildren.forEach((node) => {
      if (node.type === 'folder') initial[node.id] = !!node.active
    })
    return initial
  })

  // Open the folder that contains the active page after a route change.
  const activeFolderKey = visibleChildren
    .filter((node) => node.type === 'folder' && node.active)
    .map((node) => node.id)
    .join('|')

  useEffect(() => {
    if (!activeFolderKey) return
    const ids = activeFolderKey.split('|')
    setExpandedFolders((prev) => {
      let changed = false
      const next = { ...prev }
      ids.forEach((id) => {
        if (!next[id]) {
          next[id] = true
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [activeFolderKey])

  const handleNavigate = (path) => {
    if (path) history.push(path)
  }

  const handleToggleFolder = (id) => {
    setExpandedFolders((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const renderLeaf = (node, isChild) => (
    <Item key={node.id}>
      <RowButton
        id={`settings-panel-item-${node.id}`}
        type="button"
        role="menuitem"
        $active={node.active}
        $child={isChild}
        aria-current={node.active ? 'page' : undefined}
        onClick={() => handleNavigate(node.resolvedPath)}
      >
        <RowLabel>{node.label}</RowLabel>
        {node.tag && (
          <TagSlot>
            <Tag type={node.tag} />
          </TagSlot>
        )}
      </RowButton>
    </Item>
  )

  const renderFolder = (node) => {
    const isOpen = !!expandedFolders[node.id]
    const folderChildren = Array.isArray(node.children)
      ? node.children.filter((c) => c.visible)
      : []
    return (
      <Item key={node.id}>
        <RowButton
          id={`settings-panel-folder-${node.id}`}
          type="button"
          $folder
          $activeTrail={node.active}
          aria-expanded={isOpen}
          onClick={() => handleToggleFolder(node.id)}
        >
          <RowLabel>{node.label}</RowLabel>
          <ChevronWrap $open={isOpen} aria-hidden="true">
            <ChevronDown noDefaultFill fill="currentColor" width="16px" height="16px" />
          </ChevronWrap>
        </RowButton>
        {isOpen && (
          <FolderChildren
            id={`settings-panel-folder-${node.id}-children`}
            role="group"
            aria-label={node.label}
          >
            {folderChildren.map((child) => renderLeaf(child, true))}
          </FolderChildren>
        )}
      </Item>
    )
  }

  return (
    <Root>
      <BackHeader>
        <BackButton
          id="settings-panel-back"
          type="button"
          onClick={onBack}
          aria-label="Back to main menu"
        >
          {/* Tooltip on the arrow: the row label now reads "Settings" (Figma
              626:10405), so the back affordance is surfaced on hover instead. */}
          <Tooltip content="Back to main menu" side="bottom">
            <BackIconWrap>
              {/* Figma 626:10403: the bold arrow-square-left is Blue/950 (navy),
                  matching the label — not the primary blue it used to render. */}
              <ArrowSquareLeft variant="Bold" size={24} color="var(--sidebar-item-text)" />
            </BackIconWrap>
          </Tooltip>
          <BackLabel>Settings</BackLabel>
        </BackButton>
      </BackHeader>

      <ItemList role="menu" aria-label="Settings sub-sections">
        {visibleChildren.map((node) =>
          node.type === 'folder' ? renderFolder(node) : renderLeaf(node, false)
        )}
      </ItemList>
    </Root>
  )
}

export default SettingsPanelView
